import React from 'react';
import useEntitlement from '../../hooks/useEntitlement';
import { COPY } from '../../contracts/copyMap';
import MembershipCTA from './MembershipCTA';
import CensorBar from '../oracle/CensorBar';

// Declarative paywall wrapper.
//
//   <EntitlementGate feature="routine"><Routine /></EntitlementGate>
//
// Four states, and the third is the one that is easy to get wrong:
//
//   loading   -> a marble placeholder. NOT a spinner (DESIGN.md forbids one on
//                these surfaces) and NOT the paywall — flashing "ACCESS
//                RESTRICTED" at a paying member for 200ms on every navigation
//                is worse than showing nothing.
//   error     -> "temporarily unavailable", NOT the paywall. A Firestore blip
//                must not be presented to a subscriber as a sales pitch. Same
//                distinction the server gate makes.
//   entitled  -> children
//   otherwise -> the paywall
//
// This is presentation only. The server withholds every gated payload
// independently; a user who forces this open sees an empty screen.
export default function EntitlementGate({ feature, children, compact = false }) {
  const { hasFeature, isLoading, error } = useEntitlement();

  if (isLoading) {
    return (
      <div
        className="w-full min-h-[240px] bg-vein/40 animate-pulse"
        data-testid="entitlement-gate-loading"
        aria-busy="true"
      />
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center gap-4 py-16 text-center"
        data-testid="entitlement-gate-error"
      >
        <CensorBar mode="LABEL" value="UNAVAILABLE" size="sm" />
        <p className="font-display text-bone text-base max-w-sm">
          {COPY.MEMBERSHIP_ERROR}
        </p>
      </div>
    );
  }

  if (!hasFeature(feature)) {
    return <MembershipCTA reason={feature} compact={compact} />;
  }

  return children;
}
